import axios from "axios";
import normalizeMaker from "../components/utils";

const PROXY_URL = "https://my-cors-anywhere24-3371cd77855e.herokuapp.com/";
const PSH_API_KEY = "eec0e089-e3a1-43be-af9c-7239972315d3";
const PSH_API_URL =
  "https://api.psh.eu/SimpleOrder/api/SimpleOrderApi/GetPriceList";
const PSH_STOCK_URL =
  "https://api.psh.eu/SimpleOrder/api/SimpleOrderApi/GetArticleStock";
const PSH_ARTICLE_URL =
  "https://api.psh.eu/SimpleOrder/api/SimpleOrderApi/FindArticle";

const isArticleInStock = async (pshNumber) => {
  try {
    const stockInfo = await getArticleStock(pshNumber);
    console.log(stockInfo);
    if (stockInfo?.stock.some((item) => item.amount > 0)) {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error checking stock status:", error);
    return false;
  }
};

const getPSHNumbersByHTMLContent = async (articleNumber) => {
  const searchUrl = `https://psh.eu/zoeken/?searched_type=start&q=${encodeURIComponent(
    articleNumber
  )}`;

  const response = await axios.get(PROXY_URL + searchUrl, {
    withCredentials: false,
  });

  const htmlContent = response.data;
  const regex = /\d{3}\.\d{3}\.\d{3}\.\d{3}/g;
  const matches = [...htmlContent.matchAll(regex)];
  const uniqueNumbers = [...new Set(matches.map((m) => m[0]))];
  return uniqueNumbers;
};

const fetchArticleDetails = async (pshNumber, language = "de") => {
  try {
    const response = await axios.get(PROXY_URL + PSH_ARTICLE_URL, {
      params: {
        inputQuery: pshNumber,
        language,
      },
      headers: {
        accept: "application/json",
        Authorization: PSH_API_KEY,
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching article details:", error);
    return null;
  }
};

const fetchPSHInfos = async (articleNumber, manufacturer) => {
  try {
    const pshNumbers = await getPSHNumbersByHTMLContent(articleNumber);
    if (pshNumbers.length > 0) {
      console.log("Found PSH numbers:", pshNumbers);

      for (const pshNumber of pshNumbers) {
        console.log("Checking PSH number:", pshNumber);
        const inStock = await isArticleInStock(pshNumber);

        if (inStock) {
          const articleDetails = await fetchArticleDetails(pshNumber);
          // If we have article details and manufacturer is specified,
          // check if the normalized maker matches
          const normalizedMaker = normalizeMaker(articleDetails?.maker);
          const normalizedManufacturer = normalizeMaker(manufacturer);
          if (
            manufacturer &&
            normalizedMaker &&
            !normalizedMaker.includes(normalizedManufacturer)
          ) {
            console.log(
              `Skipping ${pshNumber} - maker ${articleDetails.maker} doesn't match ${manufacturer}`
            );
            continue;
          }

          const priceInfo = await fetchPriceByPshNumber(pshNumber);
          return {
            articleNumber,
            found: true,
            pshNumber,
            inStock: true,
            price: priceInfo.price,
            currency: priceInfo.currency,
            productUrl: priceInfo.productUrl,
            maker: articleDetails?.maker,
            name: articleDetails?.name,
            productGroup: articleDetails?.productGroup,
            specifications: articleDetails?.specifications,
          };
        }
        console.log("PSH article " + pshNumber + " not in stock.");
      }
    }

    console.log("No PSH number found in response");
    return {
      articleNumber,
      found: false,
      error: "PSH number not found",
    };
  } catch (error) {
    console.error("Error details:", {
      message: error.message,
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data
        ? error.response.data.substring(0, 200)
        : null,
    });
    return {
      articleNumber,
      found: false,
      error: `Failed to fetch PSH data: ${error.message}`,
    };
  }
};

const fetchPriceByPshNumber = async (pshNumber) => {
  const response = await axios.get(PROXY_URL + PSH_API_URL, {
    params: {
      take: 1,
      skip: 0,
      filter: `psh_number:${pshNumber}`,
    },
    headers: {
      accept: "application/json",
      Authorization: PSH_API_KEY,
    },
    withCredentials: false,
  });

  const item = response.data?.documents?.[0];
  if (item) {
    return {
      price: item.pricing.price,
      currency: item.pricing.currency,
      productUrl: `https://psh.eu/zoeken/?searched_type=start&q=${encodeURIComponent(
        pshNumber
      )}`,
    };
  } else {
    throw new Error("No pricing document found");
  }
};

/**
 * Fetch stock information for a given PSH number
 * @param {string} pshNumber - PSH article number to query
 * @param {string} [language="de"] - Language parameter for the API
 * @returns {Promise<Object>} Stock response from the API
 */
const getArticleStock = async (pshNumber, language = "de") => {
  try {
    const response = await axios.get(PROXY_URL + PSH_STOCK_URL, {
      params: {
        language,
        inputQuery: pshNumber,
      },
      headers: {
        accept: "application/json",
        Authorization: PSH_API_KEY,
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    console.error("Error getting article stock:", {
      message: error.message,
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data
        ? JSON.stringify(error.response.data).substring(0, 200)
        : null,
    });
    throw error;
  }
};

const pshService = {
  fetchPSHInfos,
  getArticleStock,
  isArticleInStock,
};

export default pshService;
